import { contextStore } from "../auth/context-store.js";
import { router } from "../router/router.js";
import { NDT_REPORT_TYPE_LABELS, type NdtReportRow, type NdtReportType } from "../../application/services/ndt-report-catalog-service.js";
import type { NdtReportStatus } from "../../domain/ndt/ndt-report-common.js";

interface Option { id: string; label: string }

const STATUS_LABELS: Record<NdtReportStatus, string> = {
  DRAFT: "Rascunho",
  IN_PROGRESS: "Em elaboração",
  VALIDATED: "Validado",
  SIGNED: "Assinado",
  FINALIZED: "Finalizado"
};

/**
 * CENTRAL DE RELATÓRIOS END (Bloco 05).
 *
 * Tela de consulta pura: agrega ME, LP, PM e US-Convencional através de
 * `NdtReportCatalogService`, que já resolve RBAC por tipo. Nunca cria, edita
 * ou gera documento — Visualizar/Baixar levam à própria página oficial do
 * relatório (mesma que já tem Gerar PDF), sem duplicar nada.
 */
class AppNdtReportsCentralPage extends HTMLElement {
  private clients: Option[] = [];
  private projects: Array<Option & { clientId: string }> = [];
  private unitAreas: Array<Option & { projectId: string }> = [];

  private clientId = "";
  private projectId = "";
  private unitAreaId = "";
  private equipmentTag = "";
  private type: NdtReportType | "" = "";
  private status: NdtReportStatus | "" = "";
  private from = "";
  private to = "";

  private rows: NdtReportRow[] = [];
  private loadingFilters = true;
  private loadingResults = false;
  private error = "";

  async connectedCallback() {
    this.render();
    await this.loadFilters();
  }

  private get platform(): any {
    return (window as any).app?.platform;
  }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: this.clientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private async loadFilters() {
    this.loadingFilters = true;
    this.render();
    try {
      const clients: any[] = (await this.platform?.adminService?.listAuthorizedClients(this.ctx()).catch(() => [])) || [];
      this.clients = clients.map(c => ({ id: String(c.id), label: c.fantasyName || c.name || String(c.id) }));
      if (!this.clientId && this.clients.length === 1) this.clientId = this.clients[0]!.id;
      await this.loadProjects();
    } finally {
      this.loadingFilters = false;
      this.render();
    }
  }

  private async loadProjects() {
    const projects: any[] = (await this.platform?.projectService?.listProjects(this.clientId || undefined).catch(() => [])) || [];
    this.projects = projects.map(p => ({ id: String(p.id), label: [p.code, p.name].filter(Boolean).join(" - ") || String(p.id), clientId: String(p.clientId || "") }));
    await this.loadUnitAreas();
  }

  private async loadUnitAreas() {
    const filter: any = {};
    if (this.clientId) filter.clientId = this.clientId;
    if (this.projectId) filter.projectId = this.projectId;
    const units: any[] = (await this.platform?.projectService?.listUnitAreas(Object.keys(filter).length ? filter : undefined).catch(() => [])) || [];
    this.unitAreas = units.map(u => ({ id: String(u.id), label: [u.code, u.name].filter(Boolean).join(" - ") || String(u.id), projectId: String(u.projectId || "") }));
    await this.runSearch();
  }

  private async runSearch() {
    this.loadingResults = true;
    this.error = "";
    this.render();
    try {
      const filter: any = {};
      if (this.projectId) filter.projectId = this.projectId;
      if (this.unitAreaId) filter.unitAreaId = this.unitAreaId;
      if (this.equipmentTag.trim()) filter.equipmentTag = this.equipmentTag.trim();
      if (this.type) filter.type = this.type;
      if (this.status) filter.status = this.status;
      if (this.from) filter.from = this.from;
      if (this.to) filter.to = this.to;
      this.rows = await this.platform.ndtReportCatalogService.search(this.ctx(), filter);
    } catch (e: any) {
      this.rows = [];
      this.error = e?.safeMessage || e?.message || "Não foi possível consultar os relatórios de END.";
    } finally {
      this.loadingResults = false;
      this.render();
    }
  }

  private render() {
    this.innerHTML = `<style>${this.styles()}</style>
      <div class="ndtc">
        <header class="ndtc-head">
          <nav class="crumb"><span class="crumb-root">RELATÓRIOS</span><span class="crumb-sep">›</span><span class="crumb-current">RELATÓRIOS DE END</span></nav>
          <h1 class="ndtc-title">Central de Relatórios END</h1>
          <p class="ndtc-sub">Consulta consolidada de Medição de Espessura, Líquido Penetrante, Partículas Magnéticas e Ultrassom Convencional.</p>
        </header>

        ${this.filtersHtml()}
        ${this.loadingResults ? this.skeleton() : this.error ? this.emptyCard("Não foi possível carregar", this.error) : this.resultsHtml()}
      </div>`;
    this.bind();
  }

  private filtersHtml(): string {
    const projects = this.clientId ? this.projects.filter(p => p.clientId === this.clientId) : this.projects;
    const units = this.projectId ? this.unitAreas.filter(u => u.projectId === this.projectId) : this.unitAreas;
    return `
      <section class="filters" aria-label="Filtros">
        <label class="filter">
          <span class="filter-label">Cliente</span>
          <select class="ctl" id="f-client" ${this.loadingFilters ? "disabled" : ""}>
            <option value="">Selecione…</option>
            ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.clientId === c.id ? "selected" : ""}>${this.esc(c.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Projeto</span>
          <select class="ctl" id="f-project" ${this.loadingFilters ? "disabled" : ""}>
            <option value="">Todos</option>
            ${projects.map(p => `<option value="${this.esc(p.id)}" ${this.projectId === p.id ? "selected" : ""}>${this.esc(p.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Unidade / Área</span>
          <select class="ctl" id="f-unit" ${this.loadingFilters ? "disabled" : ""}>
            <option value="">Todas</option>
            ${units.map(u => `<option value="${this.esc(u.id)}" ${this.unitAreaId === u.id ? "selected" : ""}>${this.esc(u.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Equipamento / TAG</span>
          <input class="ctl" type="text" id="f-tag" value="${this.esc(this.equipmentTag)}" placeholder="Ex.: V-101">
        </label>
        <label class="filter">
          <span class="filter-label">Tipo de Ensaio</span>
          <select class="ctl" id="f-type">
            <option value="">Todos</option>
            ${Object.entries(NDT_REPORT_TYPE_LABELS).map(([code, label]) => `<option value="${code}" ${this.type === code ? "selected" : ""}>${this.esc(label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Status</span>
          <select class="ctl" id="f-status">
            <option value="">Todos</option>
            ${Object.entries(STATUS_LABELS).map(([code, label]) => `<option value="${code}" ${this.status === code ? "selected" : ""}>${this.esc(label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Período</span>
          <span class="period">
            <input class="ctl" type="date" id="f-from" value="${this.esc(this.from)}">
            <span class="period-sep">→</span>
            <input class="ctl" type="date" id="f-to" value="${this.esc(this.to)}">
          </span>
        </label>
        <div class="filter-actions">
          <button class="btn btn-primary" id="btn-search" type="button">Filtrar</button>
        </div>
      </section>`;
  }

  private resultsHtml(): string {
    if (this.rows.length === 0) return this.emptyCard("Nenhum relatório encontrado", "Ajuste os filtros para consultar os relatórios de END.");
    return `
      <section class="card">
        <table class="ndt-table">
          <thead><tr><th>Tipo</th><th>Nº do Relatório</th><th>Revisão</th><th>Data</th><th>Projeto</th><th>Unidade/Área</th><th>TAG</th><th>Responsável</th><th>Status</th><th></th></tr></thead>
          <tbody>
            ${this.rows.map(row => `
              <tr>
                <td>${this.esc(row.typeLabel)}</td>
                <td>${this.esc(row.reportNumber)}</td>
                <td>${this.esc(row.revision)}</td>
                <td>${this.esc(row.date || "—")}</td>
                <td>${this.esc(row.projectLabel)}</td>
                <td>${this.esc(row.unitAreaLabel)}</td>
                <td><strong>${this.esc(row.equipmentTag)}</strong></td>
                <td>${this.esc(row.responsible)}</td>
                <td><span class="tag tag-${row.status.toLowerCase()}">${this.esc(STATUS_LABELS[row.status] || row.status)}</span></td>
                <td class="row-actions">
                  <button class="link-action" data-view="${this.esc(row.viewRoute)}" type="button">VISUALIZAR</button>
                  <button class="link-action" data-view="${this.esc(row.downloadRoute)}" type="button">BAIXAR</button>
                </td>
              </tr>`).join("")}
          </tbody>
        </table>
      </section>`;
  }

  private skeleton(): string {
    return `<div class="card sk"><div class="sk-line w40"></div><div class="sk-line w60 tall"></div><div class="sk-line w30"></div></div>`;
  }
  private emptyCard(title: string, desc: string): string {
    return `<div class="card empty"><div class="empty-title">${this.esc(title)}</div><div class="empty-desc">${this.esc(desc)}</div></div>`;
  }

  private bind() {
    const client = this.querySelector("#f-client") as HTMLSelectElement | null;
    client?.addEventListener("change", async () => { this.clientId = client.value; this.projectId = ""; this.unitAreaId = ""; await this.loadProjects(); });

    const project = this.querySelector("#f-project") as HTMLSelectElement | null;
    project?.addEventListener("change", async () => { this.projectId = project.value; this.unitAreaId = ""; await this.loadUnitAreas(); });

    const unit = this.querySelector("#f-unit") as HTMLSelectElement | null;
    unit?.addEventListener("change", async () => { this.unitAreaId = unit.value; await this.runSearch(); });

    (this.querySelector("#f-tag") as HTMLInputElement | null)?.addEventListener("input", e => { this.equipmentTag = (e.target as HTMLInputElement).value; });
    (this.querySelector("#f-type") as HTMLSelectElement | null)?.addEventListener("change", e => { this.type = (e.target as HTMLSelectElement).value as NdtReportType | ""; });
    (this.querySelector("#f-status") as HTMLSelectElement | null)?.addEventListener("change", e => { this.status = (e.target as HTMLSelectElement).value as NdtReportStatus | ""; });
    (this.querySelector("#f-from") as HTMLInputElement | null)?.addEventListener("change", e => { this.from = (e.target as HTMLInputElement).value; });
    (this.querySelector("#f-to") as HTMLInputElement | null)?.addEventListener("change", e => { this.to = (e.target as HTMLInputElement).value; });
    this.querySelector("#btn-search")?.addEventListener("click", () => { void this.runSearch(); });

    this.querySelectorAll<HTMLElement>("[data-view]").forEach(el => {
      el.addEventListener("click", () => { router.navigate(el.dataset["view"]!); });
    });
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .ndtc { display: flex; flex-direction: column; gap: 16px; }
      .crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .crumb-root { color: var(--ia-fg-soft, #9BA4AE); }
      .crumb-sep { color: var(--ia-fg-muted, rgba(255,255,255,0.45)); }
      .crumb-current { color: var(--ia-fg, #C8D2DC); font-weight: 600; }
      .ndtc-title { margin: 6px 0 2px; font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--ia-fg, #E7EAED); }
      .ndtc-sub { margin: 0; color: var(--ia-fg-muted, #8A939D); font-size: 0.875rem; }

      .filters { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; align-items: end; padding: 14px; border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; background: var(--ia-bg-panel, #12161A); }
      .filter { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .filter-label { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .ctl { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); color: var(--ia-fg, #DCE2E8); font-size: 0.82rem; font-family: inherit; }
      .period { display: grid; grid-template-columns: 1fr auto 1fr; gap: 6px; align-items: center; }
      .period-sep { color: var(--ia-fg-muted, #8A939D); }
      .filter-actions { display: flex; }
      .btn { padding: 9px 16px; border-radius: 8px; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; border: 1px solid transparent; }
      .btn-primary { background: var(--ia-bg-raised, #2B3A47); color: var(--ia-fg, #E7EAED); border-color: var(--ia-line, #3A4C5C); }
      .btn-primary:hover { background: var(--ia-bg-raised, #334554); }

      .card { background: var(--ia-bg-panel, #12161A); border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; padding: 16px; }
      .ndt-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
      .ndt-table th { text-align: left; font-size: 0.62rem; letter-spacing: 0.07em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); padding: 6px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.08)); }
      .ndt-table td { padding: 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.05)); color: var(--ia-fg-soft, #C3CAD1); vertical-align: middle; }
      .row-actions { display: flex; gap: 10px; white-space: nowrap; }
      .link-action { background: none; border: none; padding: 0; font-size: 0.68rem; font-weight: 600; letter-spacing: 0.05em; color: var(--ia-info-fg, #9BB4CC); cursor: pointer; }
      .link-action:hover { text-decoration: underline; }
      .tag { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.04em; }
      .tag-draft { background: rgba(198,168,106,0.18); color: var(--ia-warning-fg, #E0CFA3); }
      .tag-validated { background: rgba(110,132,158,0.18); color: var(--ia-fg-soft, #BCCDE0); }
      .tag-signed { background: rgba(110,158,126,0.18); color: var(--ia-fg, #B7D8C2); }

      .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6px; padding: 28px 12px; color: var(--ia-fg-muted, #8A939D); min-height: 96px; }
      .empty-title { font-size: 0.85rem; color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }
      .empty-desc { font-size: 0.75rem; max-width: 420px; }

      .sk { min-height: 104px; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
      .sk.tall { min-height: 200px; }
      .sk-line { height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05)); background-size: 200% 100%; animation: sk 1.2s ease-in-out infinite; }
      .sk-line.tall { height: 24px; }
      .w30 { width: 30%; } .w40 { width: 40%; } .w60 { width: 60%; }
      @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      @media (max-width: 1100px) { .filters { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 640px) { .filters { grid-template-columns: 1fr; } }`;
  }
}

if (!customElements.get("app-ndt-reports-central-page")) {
  customElements.define("app-ndt-reports-central-page", AppNdtReportsCentralPage);
}
